import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Sidebar } from "@/components/Sidebar";
import { StatusBar, type PreviewState } from "@/components/StatusBar";
import { MessageList } from "@/components/MessageList";
import { InputArea } from "@/components/InputArea";
import { BottomPanel } from "@/components/BottomPanel";
import { NewProjectDialog } from "@/components/NewProjectDialog";
import { AuthDialog } from "@/components/AuthDialog";
import { TerminalAuthCard } from "@/components/TerminalAuthCard";
import { SettingsPanel } from "@/components/SettingsPanel";
import { ToastContainer } from "@/components/ToastContainer";
import { WorkspaceBootScreen } from "@/components/WorkspaceBootScreen";
import WebOnboardingFlow from "@web/WebOnboardingFlow";
import TeamWorkspaceDialog from "@web/TeamWorkspaceDialog";
import {
  providerLabel,
  shortWorkspaceId,
  workspaceStatusDetail,
} from "@web/lib/workspace-display";
import {
  getAutoPreviewPort,
  getLivePreviewPorts,
  getPreviewHost,
  getPreviewUrl,
  hasAnyLivePreview,
  isPreviewPortLive,
} from "@/lib/preview-state";
import { pickAutoApproveOption } from "@/lib/permission-presets";
import {
  getActiveAcpSessionId,
  prepareMessagesForPersistence,
  resolvePersistedActiveSessionId,
} from "@/lib/chat-persistence";
import { humanizeErrorMessage } from "@/lib/error-display";
import {
  defaultHomeForProvider,
  defaultWorkingDirectoryForProject,
  type OnboardingDraft,
  type OnboardingSelection,
  type ProjectOption,
  type WorkspaceProvider,
  type WorkspaceRef,
  workspaceKey,
} from "@web/lib/onboarding";
import {
  buildPreviewUiModel,
  type PreviewAssistError,
  type PreviewAssistPhase,
} from "@web/lib/preview-ui";
import {
  buildPreviewState,
  mergePreviewPortProxyInfo,
  type PortProxyMetadata,
} from "@web/lib/preview-support";
import { mergeDiscoveredSessions } from "@web/lib/cloud-session-merge";
import {
  buildCloudSessionRestorePlan,
  deriveInitialCloudShellState,
  deriveWorkspacePersistencePlan,
  shouldShowBootInstallProgress,
  shouldShowWorkspaceBootOverlay,
  shouldAttemptCloudAgentAutoStart,
} from "@web/lib/cloud-runtime-recovery";
import { restoreBackendSessionState } from "@web/lib/cloud-session-restore";
import { useAgent } from "@/hooks/useAgent";
import { useChat } from "@/hooks/useChat";
import { useDebugLog } from "@/hooks/useDebugLog";
import { useToast } from "@/hooks/useToast";
import type {
  AgentState,
  AgentSessionInfo,
  ChatMessage,
  ChatSession,
  PtyConnectionStatus,
  RuntimeState,
} from "@/types";

interface User {
  id: string;
  email: string;
  name: string;
  roles: string;
  org: string;
}

type BottomTab = "terminal" | "debug";

type DevDebugEntry = {
  label: string;
  value: string;
  tone?: "default" | "accent" | "warn";
};

type AppView = "onboarding" | "booting" | "app";

type BootState = {
  stage: RuntimeState["stage"];
  progress: number;
  headline: string;
  detail: string;
  error?: string | null;
  errorDetails?: string | null;
};

type AgentLaunchOutcome = {
  state: "ready" | "auth_required";
  terminalAuth: boolean;
};

type AgentLaunchFailure = {
  message: string;
  details: string | null;
  source: "auto" | "manual";
  attempts: number;
};

type PreviewAssistState = {
  phase: PreviewAssistPhase;
  error: PreviewAssistError;
  requestedAt: number | null;
  attempts: number;
};

const MAX_AUTO_START_ATTEMPTS = 3;

function formatDebugValue(value: unknown): string {
  if (value == null) return "none";
  if (typeof value === "string") return value.trim() || "none";
  if (typeof value === "number" || typeof value === "boolean") return String(value);

  try {
    const json = JSON.stringify(value);
    return json.length > 140 ? `${json.slice(0, 139)}...` : json;
  } catch {
    return String(value);
  }
}

function parseStoredJson(raw: string | null): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function hasAdminRole(roles?: string): boolean {
  if (!roles) return false;
  return roles
    .split(",")
    .map((role) => role.trim().toLowerCase())
    .includes("admin");
}

function formatOnboardingBootError(error: unknown): { message: string; details: string | null } {
  const message = error instanceof Error ? error.message : String(error || "");
  const normalized = message.toLowerCase();

  if (normalized.includes("spawn gcloud enoent")) {
    return {
      message: [
        "GCP Compute startup needs the Google Cloud CLI, but `gcloud` is not installed or is not on PATH for the web dev server.",
        "Run `./setup.sh` from the repo root, or install `gcloud` and authenticate with `gcloud auth login`, then restart `cd web && bun run dev`.",
      ].join(" "),
      details: message || null,
    };
  }

  if (normalized.includes("gcloud auth login") || normalized.includes("auth is unavailable")) {
    return {
      message: [
        "GCP Compute is configured, but the local web server is not authenticated with Google Cloud.",
        "Run `gcloud auth login` and restart `cd web && bun run dev`.",
      ].join(" "),
      details: message || null,
    };
  }

  if (normalized.includes("sprites") && normalized.includes("missing")) {
    return {
      message: [
        "Sprites is selected, but the server is missing a Sprites token.",
        "Set `SPRITES_TOKEN` or switch the workspace provider before retrying.",
      ].join(" "),
      details: message || null,
    };
  }

  if (normalized.includes("session_write_busy") || normalized.includes("already writing to this session") || normalized.includes("session scope")) {
    return {
      message: "Another client is already updating this session. Please try again in a moment.",
      details: message || null,
    };
  }

  return {
    message: message || "Unable to finish onboarding",
    details: message || null,
  };
}

function buildLocalBootState(
  stage: NonNullable<RuntimeState["stage"]>,
  detail?: string,
): BootState {
  switch (stage) {
    case "creating_workspace":
      return {
        stage,
        progress: 8,
        headline: "Creating workspace",
        detail: detail || "Provisioning the workspace and preparing the runtime.",
      };
    case "waiting_for_workspace":
      return {
        stage,
        progress: 18,
        headline: "Preparing workspace",
        detail: detail || "Waiting for the workspace-local core to become reachable.",
      };
    default:
      return {
        stage,
        progress: 10,
        headline: "Preparing workspace",
        detail: detail || "Getting the workspace ready.",
      };
  }
}

function classifyPreviewFailure(messages: ChatMessage[], requestedAt: number | null) {
  if (!requestedAt) return null;

  const recentMessages = messages
    .filter((message) => message.timestamp >= requestedAt)
    .slice()
    .reverse();

  const failureMessage = recentMessages.find((message) => {
    if (message.role === "tool") {
      return message.toolStatus === "error" || /enoent|error|failed|no such file/i.test(message.content);
    }
    return message.role === "agent" && /^error:/i.test(message.content);
  });

  if (!failureMessage) return null;

  const content = failureMessage.content.toLowerCase();
  if (content.includes("connection dropped") || content.includes("did not recover")) {
    return { phase: "connection_lost" as const, error: "connection_dropped" as const };
  }

  if (content.includes("package.json") || content.includes("enoent") || content.includes("no such file or directory")) {
    return { phase: "failed" as const, error: "wrong_folder" as const };
  }

  if (content.includes("missing script") || content.includes("command not found") || content.includes("could not find a script")) {
    return { phase: "failed" as const, error: "no_start_command" as const };
  }

  return { phase: "unavailable" as const, error: "unknown" as const };
}

export default function CloudApp() {
  const [user, setUser] = useState<User | null>(null);
  const isAdmin = hasAdminRole(user?.roles);
  const isDevMode = import.meta.env.DEV || user?.email?.endsWith("@freshwaterfutures.com");
  const [loading, setLoading] = useState(true);
  const [setupComplete, setSetupComplete] = useState(false);
  const [appView, setAppView] = useState<AppView>("onboarding");
  const [bootState, setBootState] = useState<BootState | null>(null);
  const [bootSelection, setBootSelection] = useState<OnboardingSelection | null>(null);
  const [queuedAgentAfterWorkspaceConnect, setQueuedAgentAfterWorkspaceConnect] = useState<string | null>(null);
  const [onboardingDraft, setOnboardingDraft] = useState<OnboardingDraft>({
    agentId: null,
    projectId: null,
    projectName: "",
    starterProject: null,
    workspaceMode: "create",
    existingWorkspaceKey: null,
    createProvider: "daytona",
    createShared: true,
  });
  const [showBottomPanel, setShowBottomPanel] = useState(false);
  const [configuredProvider, setConfiguredProvider] = useState<WorkspaceProvider>("daytona");
  const [bottomTab, setBottomTab] = useState<BottomTab>("terminal");
  const [showPreview, setShowPreview] = useState(false);
  const [previewPort, setPreviewPort] = useState<number | null>(null);
  const [previewAssist, setPreviewAssist] = useState<PreviewAssistState>({
    phase: "idle",
    error: null,
    requestedAt: null,
    attempts: 0,
  });
  const [agentLaunchFailure, setAgentLaunchFailure] = useState<AgentLaunchFailure | null>(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const autoRanTerminalAuthRef = useRef<string | null>(null);
  const agentAutoStartLastAttemptAtRef = useRef(0);
  const autoStartAttemptCountRef = useRef(0);
  const autoStartKeyRef = useRef<string | null>(null);
  const autoStartInFlightKeyRef = useRef<string | null>(null);
  const autoStartExhaustedKeyRef = useRef<string | null>(null);
  const agentLaunchInFlightRef = useRef<Promise<AgentLaunchOutcome> | null>(null);
  const agentLaunchInFlightKeyRef = useRef<string | null>(null);
  const recentAgentErrorsRef = useRef(new Map<string, { lastAt: number; suppressed: number }>());
  const workspaceDisconnectPendingRef = useRef(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const debugLogHydratedSessionRef = useRef<string | null>(null);
  const isDebugTabVisible = showBottomPanel && bottomTab === "debug";
  const agent = useAgent();
  const debugLog = useDebugLog({ visible: isDebugTabVisible });
  const toast = useToast();
  const [runtimeState, setRuntimeState] = useState<RuntimeState>({
    status: "idle",
    reason: null,
    stage: null,
    progress: 0,
    headline: null,
    detail: null,
    error: null,
    workspace: {
      provider: null,
      workspaceId: null,
    },
  });

  const recordAgentLaunchFailure = useCallback((error: unknown, source: AgentLaunchFailure["source"], attempts: number) => {
    const { message, details } = formatOnboardingBootError(error);
    setAgentLaunchFailure({ message, details, source, attempts });
    setShowBottomPanel(true);
    setBottomTab("debug");
    return { message, details };
  }, []);

  const resetAgentLaunchFailure = useCallback(() => {
    autoStartAttemptCountRef.current = 0;
    agentAutoStartLastAttemptAtRef.current = 0;
    autoStartExhaustedKeyRef.current = null;
    setAgentLaunchFailure(null);
  }, []);

  useEffect(() => {
    if (agent.status !== "ready") return;
    autoStartAttemptCountRef.current = 0;
    agentAutoStartLastAttemptAtRef.current = 0;
    autoStartExhaustedKeyRef.current = null;
    setAgentLaunchFailure((current) => current ? null : current);
  }, [agent.status]);

  // Auto-show bottom panel during auth.
  // Terminal auth uses terminal tab; ACP-managed auth uses debug tab.
  useEffect(() => {
    if (agent.status === "authenticating") {
      setShowBottomPanel(true);
      setBottomTab(agent.isTerminalAuth ? "terminal" : "debug");
    }
  }, [agent.status, agent.isTerminalAuth]);

  // Terminal-auth flow: when ACP tells us which login command to run, open
  // the terminal panel and execute it there so the user can follow links.
  useEffect(() => {
    const challenge = agent.authChallenge;
    if (!challenge) {
      autoRanTerminalAuthRef.current = null;
      return;
    }
    const command = challenge?.terminalCommand?.trim();
    if (!command) return;

    const runKey = challenge.id || `${challenge.agentName}:${command}`;
    if (autoRanTerminalAuthRef.current === runKey) return;
    autoRanTerminalAuthRef.current = runKey;

    setShowBottomPanel(true);
    setBottomTab("terminal");

    const timer = window.setTimeout(() => {
      window.freshvibe.ptyRunCommand(command).catch((err) => {
        console.error("[CloudApp] ptyRunCommand failed:", err);
      });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [agent.authChallenge]);

  useEffect(() => {
    if (agent.status !== "authenticating") return;
    if (!agent.isTerminalAuth) return;
    if (!agent.authChallenge?.terminalCommand?.trim()) return;

    toast.clear();
    if (appView === "booting") {
      setAppView("app");
    }
    setShowBottomPanel(true);
    setBottomTab("terminal");
  }, [agent.authChallenge, agent.status, appView, agent.isTerminalAuth, toast]);

  const [workspaceDialogVisible, setWorkspaceDialogVisible] = useState(false);
  // This is the server-selected workspace ref, not proof that the VM is running
  // and not proof that `/api/core` is attached.
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceRef | null>(null);
  const [workspaceHydrated, setWorkspaceHydrated] = useState(false);
  const [portProxyMetadata, setPortProxyMetadata] = useState<PortProxyMetadata | null>(null);
  const [liveWorkspacePorts, setLiveWorkspacePorts] = useState<number[]>([]);
  const [terminalStatus, setTerminalStatus] = useState<PtyConnectionStatus>("disconnected");
  const [resettingDevState, setResettingDevState] = useState(false);
  const [workingDirectory, setWorkingDirectory] = useState<string | null>(null);

  const previewState = useMemo(
    (): PreviewState | null => buildPreviewState(
      mergePreviewPortProxyInfo(portProxyMetadata, liveWorkspacePorts),
      workingDirectory,
    ),
    [liveWorkspacePorts, portProxyMetadata, workingDirectory],
  );
  const livePreviewPorts = useMemo(() => getLivePreviewPorts(previewState), [previewState]);

  const previewUrl = useMemo(() => {
    return getPreviewUrl(previewState, previewPort);
  }, [previewState, previewPort]);

  const previewHost = useMemo(() => {
    return getPreviewHost(previewState, previewPort);
  }, [previewState, previewPort]);

  const openPreview = useCallback((port?: number, forceOpen = false) => {
    const nextPort = port ?? getAutoPreviewPort(previewState, previewPort);
    const targetPort = port ?? previewPort ?? nextPort ?? null;
    const url = getPreviewUrl(previewState, targetPort);
    if (port !== undefined) {
      setPreviewPort(port);
    } else if (nextPort !== null) {
      setPreviewPort(nextPort);
    }
    if (!url) return;

    if (!showPreview) {
      if (window.innerWidth < 1024) {
        window.open(url, "_blank");
        return;
      }
    }

    setShowPreview(forceOpen ? true : !showPreview);
  }, [previewPort, previewState, showPreview]);

  const refreshWorkspaceContext = useCallback(async () => {
    try {
      const workspaceResp = await fetch("/api/workspaces/mine", { credentials: "include" });

      if (workspaceResp.ok) {
        const data = await workspaceResp.json() as {
          selected?: WorkspaceRef | null;
          workspaces?: WorkspaceRef[];
          previewMetadata?: PortProxyMetadata | null;
        };
        if (workspaceDisconnectPendingRef.current) {
          setSelectedWorkspace(null);
          return null;
        }
        const selected = data.selected || null;
        setSelectedWorkspace(selected);
        if (data.previewMetadata) {
          setPortProxyMetadata(data.previewMetadata);
        }
        return selected;
      }
    } catch {
      // non-critical
    } finally {
      setWorkspaceHydrated(true);
    }
    return null;
  }, []);

  const disconnectWorkspacePointer = useCallback(async () => {
    await fetch("/api/workspaces/disconnect", {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
  }, []);

  const clearWorkspaceSelection = useCallback(async () => {
    workspaceDisconnectPendingRef.current = true;
    setSelectedWorkspace(null);
    setWorkspaceHydrated(true);
    setShowBottomPanel(false);
    setLiveWorkspacePorts([]);
    setPortProxyMetadata(null);
    setTerminalStatus("disconnected");
    setAppView("onboarding");
    await window.freshvibe.disconnectWorkspaceRuntime?.();
    await Promise.allSettled([
      window.freshvibe.saveSettings("workspaceProvider", null),
      window.freshvibe.saveSettings("workspaceId", null),
    ]);
    await disconnectWorkspacePointer();
    workspaceDisconnectPendingRef.current = false;
  }, [disconnectWorkspacePointer]);

  useEffect(() => {
    if (!user || appView !== "app") return;
    void refreshWorkspaceContext();
  }, [appView, user, refreshWorkspaceContext]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const persistencePlan = deriveWorkspacePersistencePlan({
      appView,
      disconnectPending: workspaceDisconnectPendingRef.current,
      workspace: selectedWorkspace
        ? {
          provider: selectedWorkspace.provider,
          resourceId: selectedWorkspace.resourceId,
        }
        : null,
    });

    if (!selectedWorkspace) {
      setLiveWorkspacePorts([]);
      setPortProxyMetadata(null);
    }

    if (persistencePlan.mode === "skip") {
      return;
    }

    if (persistencePlan.mode === "clear") {
      void Promise.allSettled([
        window.freshvibe.saveSettings("workspaceProvider", null),
        window.freshvibe.saveSettings("workspaceId", null),
      ]);
      return;
    }

    void Promise.allSettled([
      window.freshvibe.saveSettings("workspaceProvider", persistencePlan.provider),
      window.freshvibe.saveSettings("workspaceId", persistencePlan.workspaceId),
    ]);
  }, [selectedWorkspace, appView, loading]);

  useEffect(() => {
    const unsub = window.freshvibe.onPtyConnectionStatus(setTerminalStatus);
    return () => unsub?.();
  }, []);

  useEffect(() => {
    const unsub = window.freshvibe.onRuntimeStateChanged?.(setRuntimeState);
    return () => unsub?.();
  }, []);

  const hasWorkspace = Boolean(selectedWorkspace);
  const effectiveProvider: WorkspaceProvider = selectedWorkspace?.provider || configuredProvider;

  useEffect(() => {
    void fetch("/health")
      .then((response) => response.json() as Promise<{ provider?: unknown }>)
      .then((health) => {
        const provider = health.provider;
        if (provider !== "sprites" && provider !== "gcp_compute" && provider !== "e2b" && provider !== "daytona" && provider !== "lambda_microvms") return;
        setConfiguredProvider(provider);
        setOnboardingDraft((current) => current.createProvider === provider
          ? current
          : { ...current, createProvider: provider });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    void refreshWorkspaceContext();
  }, [agent.status, refreshWorkspaceContext]);

  // Real-time port updates from remote core → /api/core websocket
  useEffect(() => {
    if (!window.freshvibe.onPortsChanged) return;
    const unsub = window.freshvibe.onPortsChanged((ports: number[]) => {
      setLiveWorkspacePorts(ports);
    });
    return () => unsub?.();
  }, []);

  useEffect(() => {
    if (!window.freshvibe.onPreviewMetadataChanged) return;
    const unsub = window.freshvibe.onPreviewMetadataChanged((metadata) => {
      setPortProxyMetadata(metadata);
    });
    return () => unsub?.();
  }, []);

  useEffect(() => {
    if (!workspaceHydrated || appView !== "app" || !setupComplete) return;
    if (workspaceDisconnectPendingRef.current) return;
    if (selectedWorkspace) return;
    void clearWorkspaceSelection();
  }, [selectedWorkspace, appView, clearWorkspaceSelection, setupComplete, workspaceHydrated]);

  useEffect(() => {
    if (!previewState) {
      setPreviewPort(null);
      setShowPreview(false);
      return;
    }

    if (previewPort !== null && !isPreviewPortLive(previewState, previewPort)) {
      setPreviewPort(null);
      if (showPreview) {
        setShowPreview(false);
      }
      return;
    }

    if (showPreview && !hasAnyLivePreview(previewState)) {
      setShowPreview(false);
    }
  }, [previewPort, previewState, showPreview]);

  const syncWorkspaceState = useCallback((workspace: WorkspaceRef, announce = false) => {
    setSelectedWorkspace(workspace);
    setWorkspaceHydrated(true);
    setLiveWorkspacePorts([]);
    setPortProxyMetadata(null);

    if (announce) {
      toast.info(
        "Workspace connected",
        `${providerLabel(workspace.provider)} · ${shortWorkspaceId(workspace.resourceId, workspace.provider)}`,
      );
    }
  }, [toast]);

  const updateWorkingDirectory = useCallback(async (dir: string, persist = true, skipTerminalReset = false) => {
    setWorkingDirectory(dir);
    await window.freshvibe.setWorkingDirectory(dir, skipTerminalReset ? { skipTerminalReset: true } : undefined);
    if (persist) {
      await window.freshvibe.saveSettings("workingDirectory", dir);
    }
  }, []);

  const persistBootSuccess = useCallback(async (
    agentId: string,
    project: ProjectOption,
    cwd: string,
  ) => {
    await Promise.all([
      window.freshvibe.saveSettings("selectedAgent", agentId),
      window.freshvibe.saveSettings("selectedProject", project.id),
      window.freshvibe.saveSettings("workingDirectory", cwd),
      window.freshvibe.saveSettings("setupComplete", true),
    ]);
    setSetupComplete(true);
    setAppView("app");
  }, []);

  const bootstrapWorkspace = useCallback(async (selection: OnboardingSelection) => {
    const response = await fetch("/api/runtime/bootstrap", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        selection.workspace.kind === "existing"
          ? {
              action: "connect_existing",
              provider: selection.workspace.workspace.provider,
              resourceId: selection.workspace.workspace.resourceId,
            }
          : {
              action: "create_new",
              provider: selection.workspace.provider,
              projectId: selection.project.id,
              projectName: selection.projectName,
              shared: selection.workspace.shared,
            },
      ),
    });
    const data = await response.json().catch(() => ({})) as {
      success?: boolean;
      workspace?: WorkspaceRef;
      previewMetadata?: PortProxyMetadata | null;
      error?: string;
    };
    if (!response.ok || !data.success || !data.workspace) {
      throw new Error(data.error || "Failed to bootstrap workspace");
    }
    syncWorkspaceState(data.workspace);
    setPortProxyMetadata(data.previewMetadata ?? null);
    return data.workspace;
  }, [syncWorkspaceState]);

  const ensureAgentRunning = useCallback(async (
    agentId: string,
    options?: {
      workspace?: WorkspaceRef | null;
      project?: ProjectOption | null;
      persistSetup?: boolean;
      projectName?: string | null;
      starterProject?: OnboardingSelection["starterProject"] | null;
      resumeSessionId?: string;
      reason?: "cold_start" | "reconnect";
    },
  ): Promise<AgentLaunchOutcome> => {
    const runtimeWorkspace = options?.workspace ?? selectedWorkspace;
    const launchKey = JSON.stringify({
      agentId,
      workspace: runtimeWorkspace ? workspaceKey(runtimeWorkspace) : null,
      resumeSessionId: options?.resumeSessionId ?? null,
      reason: options?.reason ?? "reconnect",
      starterProject: options?.starterProject?.kind === "starter" ? options.starterProject.kitId : null,
      projectName: options?.projectName ?? null,
    });

    if (agentLaunchInFlightRef.current) {
      if (agentLaunchInFlightKeyRef.current === launchKey) {
        return await agentLaunchInFlightRef.current;
      }
      await agentLaunchInFlightRef.current.catch(() => undefined);
    }

    const launchPromise = (async (): Promise<AgentLaunchOutcome> => {
      if (!runtimeWorkspace) {
        throw new Error("No workspace selected");
      }

      agent.setSelectedAgentId(agentId);
      agent.setConnectingStatus();
      await window.freshvibe.saveSettings("selectedAgent", agentId);

      const cwd = workingDirectory || defaultHomeForProvider(runtimeWorkspace.provider);
      const result = await window.freshvibe.bootstrapRuntime?.({
        presetId: agentId,
        cwd,
        workspace: {
          provider: runtimeWorkspace.provider,
          workspaceId: runtimeWorkspace.resourceId,
        },
        starter: options?.starterProject?.kind === "starter"
          ? {
              kitId: options.starterProject.kitId,
              keepGit: options.starterProject.keepGit,
              targetFolder: options.projectName || undefined,
            }
          : null,
        resumeSessionId: options?.resumeSessionId,
        reason: options?.reason ?? "reconnect",
      });
      if (!result) {
        throw new Error("Runtime bootstrap did not return a result");
      }

      await updateWorkingDirectory(result.cwd, true, true);

      if (options?.persistSetup && options.project) {
        await persistBootSuccess(agentId, options.project, result.cwd);
      } else {
        setAppView("app");
      }

      const outcome: AgentLaunchOutcome = {
        state: result.state,
        terminalAuth: Boolean(agent.authChallenge?.terminalCommand?.trim()),
      };
      if (outcome.state === "auth_required") {
        setShowBottomPanel(true);
        setBottomTab(outcome.terminalAuth ? "terminal" : "debug");
      }

      return outcome;
    })();

    agentLaunchInFlightKeyRef.current = launchKey;
    agentLaunchInFlightRef.current = launchPromise;

    try {
      return await launchPromise;
    } finally {
      if (agentLaunchInFlightRef.current === launchPromise) {
        agentLaunchInFlightRef.current = null;
        agentLaunchInFlightKeyRef.current = null;
      }
    }
  }, [
    agent,
    persistBootSuccess,
    selectedWorkspace,
    updateWorkingDirectory,
    workingDirectory,
  ]);

  // ─── Session history state ───
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [activeAcpSessionId, setActiveAcpSessionId] = useState<string | null>(null);
  const [isSessionRestorePending, setIsSessionRestorePending] = useState(false);
  // Track the ACP session ID for the current (not-yet-saved) chat
  const pendingAcpSessionIdRef = useRef<string | null>(null);
  const bootRestoreSessionIdRef = useRef<string | null>(null);
  const sessionRestoreRunRef = useRef(0);
  const sessionRestoreQueueRef = useRef(Promise.resolve());

  // Late-bound: the handler needs `chat`, which this hook call creates.
  const backgroundTurnHandlerRef = useRef<
    ((acpSessionId: string, finalContent: string) => void) | null
  >(null);

  // ─── useChat hook — same as Electron app ───
  // Handles ACP streaming, tool calls, permissions, thinking, etc.
  const chat = useChat({
    activeSessionId,
    activeAcpSessionId,
    workingDirectory,
    agentId: agent.selectedAgent,
    modelId: agent.currentModelId,
    onBackgroundTurnCompleted: (acpSessionId, finalContent) =>
      backgroundTurnHandlerRef.current?.(acpSessionId, finalContent),
    onSessionCreated: (session) => {
      // Attach the pending ACP session ID so we can loadSession later
      const enriched = pendingAcpSessionIdRef.current
        ? { ...session, acpSessionId: pendingAcpSessionIdRef.current }
        : session;
      setSessions((prev) => {
        const updated = [enriched, ...prev];
        window.freshvibe.saveSettings("chatSessions", updated);
        return updated;
      });
      setPersistedActiveSessionId(enriched.id);
      setActiveAcpSessionId(enriched.acpSessionId || pendingAcpSessionIdRef.current || null);
    },
    onSessionUpdated: (id, messageCount, lastMessageAt) => {
      setSessions((prev) => {
        const updated = prev.map((s) =>
          s.id === id ? { ...s, messageCount, lastMessageAt } : s,
        );
        window.freshvibe.saveSettings("chatSessions", updated);
        return updated;
      });
    },
    pushDebugEntry: debugLog.pushEntry,
    saveDebugEntries: debugLog.saveEntries,
  });

  // A prompt finished for a session the user has switched away from. Persist the
  // completed turn into that session's stored transcript so switching back shows
  // the full response instead of the switch-away partial. Without this the web
  // client silently truncates a background turn at the switch-away partial,
  // since switch-back rehydrates purely from the local store.
  const handleBackgroundTurnCompleted = useCallback(
    async (acpSessionId: string, finalContent: string) => {
      if (!finalContent) return;
      const session = sessions.find((s) => s.acpSessionId === acpSessionId);
      if (!session) return;

      const saved = await window.freshvibe.getSessionMessages(session.id).catch(() => []);
      const messages = Array.isArray(saved) ? (saved as ChatMessage[]) : [];
      const last = messages[messages.length - 1];
      if (last && last.role === "agent" && last.content === finalContent) return;

      // The switch-away snapshot ends with a partial of this same turn — replace
      // it. After a mid-turn tool call the final text is not a superset of the
      // partial, so append it instead of losing it.
      const updated =
        last && last.role === "agent" && finalContent.startsWith(last.content)
          ? [...messages.slice(0, -1), { ...last, content: finalContent }]
          : [
              ...messages,
              {
                id: crypto.randomUUID(),
                role: "agent" as const,
                content: finalContent,
                timestamp: Date.now(),
              },
            ];

      await window.freshvibe.saveSessionMessages(session.id, updated);
      setSessions((prev) => {
        const next = prev.map((s) =>
          s.id === session.id
            ? { ...s, messageCount: updated.length, lastMessageAt: Date.now() }
            : s,
        );
        window.freshvibe.saveSettings("chatSessions", next);
        return next;
      });

      // Already switched back with no live chunk to re-render the turn — refresh
      // the visible transcript from the store.
      if (session.id === activeSessionId && !chat.isStreaming) {
        chat.loadMessages(updated);
      }
    },
    [sessions, activeSessionId, chat],
  );
  backgroundTurnHandlerRef.current = handleBackgroundTurnCompleted;

  const previewFailure = useMemo(
    () => classifyPreviewFailure(chat.messages, previewAssist.requestedAt),
    [chat.messages, previewAssist.requestedAt],
  );
  const previewApprovalOption = useMemo(
    () => (chat.pendingPermission ? pickAutoApproveOption(chat.pendingPermission.options) : null),
    [chat.pendingPermission],
  );

  const requestPreviewStart = useCallback((mode: "start" | "retry" = "start") => {
    setPreviewAssist((prev) => ({
      phase: mode === "retry" ? "retrying" : "starting",
      error: null,
      requestedAt: Date.now(),
      attempts: mode === "retry" ? prev.attempts + 1 : 1,
    }));

    void chat.sendMessage({
      text: "Start the app so I can preview it. Keep every update in plain English for a non-technical person. Do not mention localhost, ports, session IDs, sandboxes, or shell commands unless I ask. If approval is needed, ask for it clearly. If it fails, explain the problem and next step in plain English.",
      attachments: [],
    });
  }, [chat.sendMessage]);

  const askPreviewHelp = useCallback(() => {
    void chat.sendMessage({
      text: "Tell me, in plain English, what needs to happen so I can preview this app. Keep it non-technical and avoid localhost, ports, session IDs, sandboxes, and shell commands unless I ask. If something is wrong with the folder or start command, explain that clearly and tell me the next best step.",
      attachments: [],
    });
  }, [chat.sendMessage]);

  useEffect(() => {
    if (!previewAssist.requestedAt) return;
    if (chat.pendingPermission) {
      setPreviewAssist((prev) =>
        prev.phase === "waiting_approval"
          ? prev
          : { ...prev, phase: "waiting_approval" },
      );
      return;
    }

    if (chat.isStreaming && previewAssist.phase === "waiting_approval") {
      setPreviewAssist((prev) => ({
        ...prev,
        phase: prev.attempts > 1 ? "retrying" : "starting",
      }));
    }
  }, [chat.isStreaming, chat.pendingPermission, previewAssist.phase, previewAssist.requestedAt]);

  useEffect(() => {
    if (!previewAssist.requestedAt || !previewFailure) return;
    if (chat.pendingPermission || chat.isStreaming || chat.activeTool) return;
    if (livePreviewPorts.length > 0) return;

    setPreviewAssist((prev) => (
      prev.phase === previewFailure.phase && prev.error === previewFailure.error
        ? prev
        : { ...prev, phase: previewFailure.phase, error: previewFailure.error }
    ));
  }, [
    chat.activeTool,
    chat.isStreaming,
    chat.pendingPermission,
    livePreviewPorts.length,
    previewAssist.requestedAt,
    previewFailure,
  ]);

  useEffect(() => {
    if (!previewAssist.requestedAt) return;
    if (chat.isStreaming || chat.pendingPermission || chat.activeTool || livePreviewPorts.length > 0) return;
    if (previewAssist.phase === "failed" || previewAssist.phase === "connection_lost" || previewAssist.phase === "unavailable") return;

    const timer = window.setTimeout(() => {
      setPreviewAssist((prev) => {
        if (!prev.requestedAt) return prev;
        if (prev.phase === "failed" || prev.phase === "connection_lost" || prev.phase === "unavailable") {
          return prev;
        }
        return {
          ...prev,
          phase: "unavailable",
          error: prev.error ?? "unknown",
        };
      });
    }, 1400);

    return () => window.clearTimeout(timer);
  }, [
    chat.activeTool,
    chat.isStreaming,
    chat.pendingPermission,
    livePreviewPorts.length,
    previewAssist.phase,
    previewAssist.requestedAt,
  ]);

  useEffect(() => {
    if (!previewAssist.requestedAt || livePreviewPorts.length === 0) return;

    const nextPort = getAutoPreviewPort(previewState, previewPort);
    const targetPort = nextPort ?? previewPort ?? null;
    const url = getPreviewUrl(previewState, targetPort);
    if (nextPort !== null && previewPort !== nextPort) {
      setPreviewPort(nextPort);
    }
    if (url && !showPreview) {
      if (window.innerWidth < 1024) {
        window.open(url, "_blank");
      } else {
        setShowPreview(true);
      }
    }

    setPreviewAssist({
      phase: "idle",
      error: null,
      requestedAt: null,
      attempts: 0,
    });
  }, [livePreviewPorts.length, previewAssist.requestedAt, previewPort, previewState, showPreview]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat.messages, chat.isStreaming]);

  // Auth check
  useEffect(() => {
    let cancelled = false;

    (async () => {
      let shouldFinishLoading = true;
      let authenticated = false;

      try {
        const response = await fetch("/api/user", { credentials: "include" });
        if (response.status === 401) {
          shouldFinishLoading = false;
          window.location.replace("/api/auth/login");
          return;
        }

        if (!response.ok) {
          throw new Error(`Auth check failed (${response.status})`);
        }

        const data = await response.json() as User | { error?: string };
        if (!cancelled && !("error" in data)) {
          setUser(data as User);
          authenticated = true;
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[CloudApp] auth bootstrap failed:", err);
        }
      } finally {
        if (!cancelled && shouldFinishLoading && !authenticated) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Capture ACP session ID when agent becomes ready (initial connect or reconnect)
  useEffect(() => {
    const unsub = window.freshvibe.onAcpSessionId((acpSessionId: string) => {
      if (isSessionRestorePending) return;
      pendingAcpSessionIdRef.current = acpSessionId;
      setActiveAcpSessionId(acpSessionId);
    });
    return () => unsub?.();
  }, [isSessionRestorePending]);

  const persistSessions = useCallback((updater: (prev: ChatSession[]) => ChatSession[]) => {
    setSessions((prev) => {
      const updated = updater(prev);
      if (updated !== prev) {
        window.freshvibe.saveSettings("chatSessions", updated);
      }
      return updated;
    });
  }, []);

  const setPersistedActiveSessionId = useCallback((nextId: string | null) => {
    setActiveSessionId(nextId);
    void window.freshvibe.saveSettings("activeSessionId", nextId);
  }, []);

  const startSessionRestoreRun = useCallback(() => {
    sessionRestoreRunRef.current += 1;
    setIsSessionRestorePending(true);
    return sessionRestoreRunRef.current;
  }, []);

  const cancelSessionRestore = useCallback(() => {
    sessionRestoreRunRef.current += 1;
    setIsSessionRestorePending(false);
  }, []);

  const isSessionRestoreCurrent = useCallback((runId: number) => {
    return sessionRestoreRunRef.current === runId;
  }, []);

  const queueSessionRestore = useCallback((task: () => Promise<void>) => {
    sessionRestoreQueueRef.current = sessionRestoreQueueRef.current
      .catch(() => undefined)
      .then(task)
      .catch((err) => {
        console.error("[CloudApp] background session restore failed:", err);
      });
  }, []);

  const syncSessionsFromAgent = useCallback(async () => {
    if (agent.status !== "ready") return;
    const discovered = await window.freshvibe.listSessions().catch(() => []);
    if (!Array.isArray(discovered) || discovered.length === 0) return;
    persistSessions((prev) => mergeDiscoveredSessions(
      prev,
      discovered,
      agent.selectedAgent,
      workingDirectory,
    ));
  }, [agent.selectedAgent, agent.status, persistSessions, workingDirectory]);

  useEffect(() => {
    if (agent.status !== "ready") return;
    syncSessionsFromAgent().catch((err) => {
      console.error("[CloudApp] syncSessionsFromAgent failed:", err);
    });
  }, [agent.status, syncSessionsFromAgent]);

  // Wire up error events → toast notifications + debug log
  useEffect(() => {
    const unsubError = window.freshvibe.onError((msg: string) => {
      const now = Date.now();
      const previous = recentAgentErrorsRef.current.get(msg);
      if (previous && now - previous.lastAt < 10_000) {
        previous.lastAt = now;
        previous.suppressed += 1;
        return;
      }

      const repeatSuffix = previous && previous.suppressed > 0
        ? ` (${previous.suppressed} repeats suppressed)`
        : "";
      recentAgentErrorsRef.current.set(msg, { lastAt: now, suppressed: 0 });
      if (recentAgentErrorsRef.current.size > 50) {
        const oldest = recentAgentErrorsRef.current.keys().next().value;
        if (typeof oldest === "string") {
          recentAgentErrorsRef.current.delete(oldest);
        }
      }

      const visibleMessage = `${msg}${repeatSuffix}`;
      console.error("[CloudApp] onError:", visibleMessage);
      toast.error("Something went wrong", humanizeErrorMessage(visibleMessage));
      debugLog.pushEntry({
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        level: "error",
        source: "agent",
        message: visibleMessage,
      });
      setShowBottomPanel(true);
      setBottomTab("debug");
    });

    const unsubDebug = window.freshvibe.onDebugLog((entry: unknown) => {
      const e = entry as { level?: string; message?: string };
      if (e.level === "error") {
        setShowBottomPanel(true);
        setBottomTab("debug");
      }
    });
    return () => { unsubError?.(); unsubDebug?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore setup state + sessions from localStorage.
  // Use a ref to prevent StrictMode from firing the effect twice.
  const settingsRestored = useRef(false);
  useEffect(() => {
    if (!user) return;
    if (settingsRestored.current) return;
    settingsRestored.current = true;
    void (async () => {
      try {
        const settings = await window.freshvibe.getSettings();
        const savedSetupComplete = Boolean(settings.setupComplete);
        const savedAgentId = typeof settings.selectedAgent === "string" && settings.selectedAgent
          ? settings.selectedAgent
          : null;
        const savedProjectId = typeof settings.selectedProject === "string" && settings.selectedProject
          ? settings.selectedProject
          : null;
        const savedWorkingDirectory = typeof settings.workingDirectory === "string" && settings.workingDirectory
          ? settings.workingDirectory
          : null;

        if (savedAgentId) {
          agent.setSelectedAgentId(savedAgentId);
        }
        if (savedProjectId || savedAgentId) {
          setOnboardingDraft((prev) => ({
            ...prev,
            agentId: savedAgentId || prev.agentId,
            projectId: savedProjectId || prev.projectId,
          }));
        }
        if (savedWorkingDirectory) {
          setWorkingDirectory(savedWorkingDirectory);
        }

        const saved = (settings as unknown as Record<string, unknown>).chatSessions;
        const savedSessions = Array.isArray(saved) ? saved as ChatSession[] : [];
        if (savedSessions.length > 0) {
          setSessions(savedSessions);
        }
        bootRestoreSessionIdRef.current = resolvePersistedActiveSessionId(
          savedSessions,
          typeof settings.activeSessionId === "string" ? settings.activeSessionId : null,
        );

        const initialShellState = deriveInitialCloudShellState({
          savedSetupComplete,
          savedAgentId,
          savedWorkingDirectory,
        });
        setSetupComplete(initialShellState.setupComplete);
        setAppView(initialShellState.appView);
        setLoading(false);

        const selectedWorkspace = await refreshWorkspaceContext();

        const state: AgentState | null = selectedWorkspace && window.freshvibe.getAgentState
          ? await window.freshvibe.getAgentState().catch(() => null)
          : null;
        if (state?.connected && state.presetId && state.cwd) {
          setSetupComplete(true);
          setAppView("app");
          setWorkingDirectory(state.cwd);
          agent.setSelectedAgentId(state.presetId);
          pendingAcpSessionIdRef.current = state.sessionId || null;
          setActiveAcpSessionId(state.sessionId || null);
          await Promise.all([
            window.freshvibe.saveSettings("setupComplete", true),
            window.freshvibe.saveSettings("selectedAgent", state.presetId),
            window.freshvibe.saveSettings("workingDirectory", state.cwd),
            window.freshvibe.setWorkingDirectory(state.cwd),
          ]);
          if (window.freshvibe.connectAgentWs) {
            await window.freshvibe.connectAgentWs().catch(() => {});
          }
        }
      } catch (err) {
        console.error("[CloudApp] restore bootstrap failed:", err);
        setSetupComplete(false);
        setAppView("onboarding");
      } finally {
        setLoading(false);
      }
    })();
  }, [agent, refreshWorkspaceContext, user]);

  // Auto-start the agent once workspace is ready and we have a saved agent
  // but no active connection (e.g. page reload without server-side restore).
  const autoStartKey = selectedWorkspace && agent.selectedAgent
    ? `${workspaceKey(selectedWorkspace)}:${agent.selectedAgent}`
    : null;

  useEffect(() => {
    if (autoStartKeyRef.current === autoStartKey) return;
    autoStartKeyRef.current = autoStartKey;
    autoStartInFlightKeyRef.current = null;
    autoStartAttemptCountRef.current = 0;
    agentAutoStartLastAttemptAtRef.current = 0;
    autoStartExhaustedKeyRef.current = null;
    setAgentLaunchFailure(null);
  }, [autoStartKey]);

  useEffect(() => {
    if (agentLaunchFailure?.source === "manual") return;
    const selectedAgent = agent.selectedAgent;
    if (!selectedAgent || !autoStartKey) return;
    if (autoStartKeyRef.current !== autoStartKey) return;
    if (autoStartExhaustedKeyRef.current === autoStartKey) return;
    if (autoStartInFlightKeyRef.current === autoStartKey) return;
    if (!shouldAttemptCloudAgentAutoStart({
      loading,
      appView,
      hasWorkspace,
      selectedAgent,
      agentStatus: agent.status,
      lastAttemptAt: agentAutoStartLastAttemptAtRef.current,
      now: Date.now(),
      cooldownMs: 5_000,
      attempts: autoStartAttemptCountRef.current,
      maxAttempts: MAX_AUTO_START_ATTEMPTS,
    })) {
      return;
    }

    autoStartInFlightKeyRef.current = autoStartKey;

    void (async () => {
      try {
        const runtimeState = await window.freshvibe.getAgentState().catch(() => ({
          connected: false,
          presetId: null,
        }));
        if (autoStartKeyRef.current !== autoStartKey) return;
        if (runtimeState?.connected && runtimeState.presetId === selectedAgent) {
          resetAgentLaunchFailure();
          return;
        }

        agentAutoStartLastAttemptAtRef.current = Date.now();
        autoStartAttemptCountRef.current += 1;

        const restoreSessionId = activeSessionId || bootRestoreSessionIdRef.current;
        const resumeSessionId = getActiveAcpSessionId(sessions, restoreSessionId);
        const outcome = await ensureAgentRunning(
          selectedAgent,
          {
            ...(resumeSessionId ? { resumeSessionId } : {}),
            reason: "reconnect",
          },
        );
        if (autoStartKeyRef.current !== autoStartKey) return;
        if (outcome.state === "ready" || outcome.state === "auth_required") {
          resetAgentLaunchFailure();
        }
      } catch (err) {
        if (autoStartKeyRef.current !== autoStartKey) return;
        const attempts = autoStartAttemptCountRef.current;
        recordAgentLaunchFailure(err, "auto", attempts);
        if (attempts >= MAX_AUTO_START_ATTEMPTS) {
          autoStartExhaustedKeyRef.current = autoStartKey;
        }
        console.error(`[CloudApp] Agent auto-start failed (attempt ${attempts}/${MAX_AUTO_START_ATTEMPTS}):`, err);
      } finally {
        if (autoStartInFlightKeyRef.current === autoStartKey) {
          autoStartInFlightKeyRef.current = null;
        }
      }
    })();
  }, [
    activeSessionId,
    autoStartKey,
    appView,
    ensureAgentRunning,
    hasWorkspace,
    loading,
    sessions,
    agent.selectedAgent,
    agent.status,
    agentLaunchFailure?.source,
    recordAgentLaunchFailure,
    resetAgentLaunchFailure,
  ]);

  const handleStartOnboarding = useCallback(async (selection: OnboardingSelection) => {
    setBootSelection(selection);
    setBootState(buildLocalBootState(
      selection.workspace.kind === "create" ? "creating_workspace" : "waiting_for_workspace",
    ));
    setAppView("booting");
    setShowBottomPanel(false);
    setQueuedAgentAfterWorkspaceConnect(null);
    setSetupComplete(false);
    setOnboardingDraft((prev) => ({
      ...prev,
      agentId: selection.agentId,
      projectId: selection.project.id,
      starterProject: selection.starterProject,
      workspaceMode: selection.workspace.kind,
      existingWorkspaceKey: selection.workspace.kind === "existing"
        ? workspaceKey(selection.workspace.workspace)
        : null,
      createProvider: selection.workspace.kind === "create"
        ? selection.workspace.provider
        : prev.createProvider,
      createShared: selection.workspace.kind === "create"
        ? selection.workspace.shared
        : prev.createShared,
    }));

    try {
      await Promise.all([
        window.freshvibe.saveSettings("selectedAgent", selection.agentId),
        window.freshvibe.saveSettings("selectedProject", selection.project.id),
      ]);
      agent.setSelectedAgentId(selection.agentId);

      const workspace = await bootstrapWorkspace(selection);
      setBootSelection({
        ...selection,
        workspace: {
          kind: "existing",
          workspace,
        },
      });

      const initialCwd = selection.starterProject.kind === "starter"
        ? defaultHomeForProvider(workspace.provider)
        : defaultWorkingDirectoryForProject(workspace.provider, selection.projectName);
      await updateWorkingDirectory(initialCwd, true, true);

      await ensureAgentRunning(selection.agentId, {
        workspace,
        project: selection.project,
        persistSetup: true,
        projectName: selection.projectName,
        starterProject: selection.starterProject,
        reason: "cold_start",
      });

      setBootState(null);
    } catch (err) {
      const { message, details } = formatOnboardingBootError(err);
      console.error("[CloudApp] onboarding boot failed:", err);
      setBootState((current) => ({
        ...(current || buildLocalBootState("waiting_for_workspace")),
        error: message,
        errorDetails: details,
      }));
      setShowBottomPanel(true);
      setBottomTab("debug");
    }
  }, [
    agent,
    bootstrapWorkspace,
    ensureAgentRunning,
    updateWorkingDirectory,
  ]);

  const handleRetryBoot = useCallback(() => {
    if (!bootSelection) return;
    void handleStartOnboarding(bootSelection);
  }, [bootSelection, handleStartOnboarding]);

  const handleChooseDifferentWorkspace = useCallback(() => {
    setBootState(null);
    setShowBottomPanel(false);
    void clearWorkspaceSelection();
    setOnboardingDraft((prev) => {
      if (!bootSelection) {
        return {
          ...prev,
          existingWorkspaceKey: null,
        };
      }

      if (bootSelection.workspace.kind === "existing") {
        return {
          ...prev,
          workspaceMode: "existing",
          existingWorkspaceKey: null,
        };
      }

      return {
        ...prev,
        workspaceMode: "create",
        existingWorkspaceKey: null,
        createProvider: bootSelection.workspace.provider,
        createShared: bootSelection.workspace.shared,
      };
    });
  }, [bootSelection, clearWorkspaceSelection]);

  // ─── Agent / model change handlers ───

  const handleChangeAgent = useCallback(
    async (agentId: string) => {
      if (!hasWorkspace) {
        setQueuedAgentAfterWorkspaceConnect(agentId);
        agent.setSelectedAgentId(agentId);
        await window.freshvibe.saveSettings("selectedAgent", agentId);
        setWorkspaceDialogVisible(true);
        setShowSettings(false);
        toast.warning(
          "Workspace required",
          "Create or connect a workspace before starting an agent.",
        );
        return;
      }

      try {
        // Immediately reflect the new agent in the dropdown with amber status
        agent.setSelectedAgentId(agentId);
        agent.setConnectingStatus();
        await window.freshvibe.saveSettings("selectedAgent", agentId);
        await ensureAgentRunning(agentId);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to start agent";
        toast.error("Agent start failed", message);
      } finally {
        setShowSettings(false);
      }
    },
    [agent, ensureAgentRunning, hasWorkspace, toast],
  );

  const handleChangeModel = useCallback(
    async (modelId: string) => {
      const success = await agent.setModel(modelId);
      if (!success) return;
      if (activeSessionId) {
        setSessions((prev) => {
          const updated = prev.map((s) =>
            s.id === activeSessionId ? { ...s, modelId } : s,
          );
          window.freshvibe.saveSettings("chatSessions", updated);
          return updated;
        });
      }
    },
    [agent, activeSessionId],
  );

  const handleLogout = useCallback(async () => {
    await agent.logoutAgent();
  }, [agent]);

  const handleAuthLogout = useCallback(() => {
    window.location.assign("/api/auth/logout");
  }, []);

  const handleReconnect = useCallback(async () => {
    if (!agent.selectedAgent) return;
    if (!hasWorkspace) {
      setQueuedAgentAfterWorkspaceConnect(agent.selectedAgent);
      setWorkspaceDialogVisible(true);
      toast.warning(
        "Workspace required",
        "Create or connect a workspace before reconnecting the agent.",
      );
      return;
    }

    // Capture the previous ACP session ID before reconnecting so we can resume it
    const previousAcpSessionId = activeAcpSessionId || pendingAcpSessionIdRef.current;
    resetAgentLaunchFailure();

    try {
      agent.setConnectingStatus();
      // Pass the previous session ID so the server can attempt to resume it
      // in a single roundtrip after the agent starts
      await ensureAgentRunning(agent.selectedAgent, {
        resumeSessionId: previousAcpSessionId || undefined,
        reason: "reconnect",
      });
    } catch (err) {
      const { message } = recordAgentLaunchFailure(err, "manual", 1);
      toast.error("Reconnect failed", message);
    }
  }, [agent, activeAcpSessionId, ensureAgentRunning, hasWorkspace, recordAgentLaunchFailure, resetAgentLaunchFailure, toast]);

  const handlePreviewAction = useCallback((itemId?: string) => {
    if (!itemId) {
      openPreview();
      return;
    }

    if (itemId.startsWith("open:")) {
      const port = Number(itemId.slice("open:".length));
      if (Number.isInteger(port) && port > 0) {
        openPreview(port, true);
      }
      return;
    }

    if (itemId === "start_app") {
      requestPreviewStart("start");
      return;
    }

    if (itemId === "try_again") {
      requestPreviewStart("retry");
      return;
    }

    if (itemId === "ask_freshvibe") {
      askPreviewHelp();
      return;
    }

    if (itemId === "approve") {
      void chat.respondToPermission(previewApprovalOption?.optionId ?? null);
      return;
    }

    if (itemId === "cancel") {
      void chat.respondToPermission(null);
      return;
    }

    if (itemId === "reconnect") {
      void handleReconnect();
      return;
    }

    if (itemId === "open_terminal") {
      setShowBottomPanel(true);
      setBottomTab("debug");
    }
  }, [
    askPreviewHelp,
    chat.respondToPermission,
    handleReconnect,
    openPreview,
    previewApprovalOption?.optionId,
    requestPreviewStart,
  ]);

  const previewUi = useMemo(
    () => buildPreviewUiModel({
      preview: previewState,
      previewVisible: showPreview,
      activePorts: livePreviewPorts,
      assistPhase: previewAssist.phase,
      assistError: previewAssist.error,
    }),
    [livePreviewPorts, previewAssist.error, previewAssist.phase, previewState, showPreview],
  );

  const handleChangeDirectory = useCallback(async () => {
    const dir = await window.freshvibe.selectWorkingDirectory();
    if (dir) {
      await updateWorkingDirectory(dir);
    }
  }, [updateWorkingDirectory]);

  const workspaceBootOverlay = useMemo(() => {
    if (agentLaunchFailure && appView === "app") {
      const attemptDetail = agentLaunchFailure.source === "auto"
        ? agentLaunchFailure.attempts >= MAX_AUTO_START_ATTEMPTS
          ? `Automatic reconnect stopped after ${MAX_AUTO_START_ATTEMPTS} attempts.`
          : `Automatic reconnect attempt ${agentLaunchFailure.attempts} of ${MAX_AUTO_START_ATTEMPTS} failed.`
        : "The agent could not be reconnected.";
      return {
        mode: "reconnecting" as const,
        state: {
          stage: "starting_agent" as const,
          progress: Math.max(88, Math.min(99, runtimeState.progress || 88)),
          headline: agentLaunchFailure.source === "auto"
            ? "Could not reconnect to the agent"
            : "Reconnect failed",
          detail: `${attemptDetail} Retry the connection or choose a different workspace.`,
          error: agentLaunchFailure.message,
        },
        errorDetails: agentLaunchFailure.details,
        onRetry: handleReconnect,
      };
    }

    const runtimeOverlayState = shouldShowWorkspaceBootOverlay({
      appView,
      runtimeStatus: runtimeState.status,
      runtimeStage: runtimeState.stage,
      terminalAuthActive: agent.isTerminalAuth,
    })
      ? {
          stage: runtimeState.stage ?? "starting_agent",
          progress: runtimeState.progress,
          headline: runtimeState.headline || "Preparing workspace",
          detail: runtimeState.detail || "Waiting for the workspace runtime.",
          error: runtimeState.error
            ? humanizeErrorMessage(runtimeState.error)
            : null,
        }
      : null;

    if (runtimeOverlayState && (appView === "booting" || appView === "app")) {
      return {
        mode: runtimeState.reason === "reconnect" ? "reconnecting" as const : "starting" as const,
        state: runtimeOverlayState,
        errorDetails: runtimeState.error,
        onRetry: bootSelection && appView === "booting" ? handleRetryBoot : handleReconnect,
      };
    }

    if (appView === "booting" && bootState) {
      return {
        mode: "starting" as const,
        state: bootState,
        errorDetails: bootState.errorDetails ?? null,
        onRetry: bootSelection ? handleRetryBoot : undefined,
      };
    }

    return null;
  }, [
    appView,
    bootSelection,
    bootState,
    handleReconnect,
    handleRetryBoot,
    runtimeState,
    agent.isTerminalAuth,
    agentLaunchFailure,
  ]);

  const overlayInstallProgress = useMemo(() => {
    if (!workspaceBootOverlay) {
      return null;
    }

    return shouldShowBootInstallProgress({
      stage: workspaceBootOverlay.state.stage,
      installStep: agent.installProgress?.step ?? null,
    })
      ? agent.installProgress
      : null;
  }, [agent.installProgress, workspaceBootOverlay]);

  const handleWorkspaceConnected = useCallback(async (workspace: WorkspaceRef) => {
    syncWorkspaceState(workspace, true);
    void refreshWorkspaceContext();

    const nextAgentId = queuedAgentAfterWorkspaceConnect || agent.selectedAgent;
    setQueuedAgentAfterWorkspaceConnect(null);
    if (!nextAgentId) return;

    if (!workingDirectory) {
      const defaultDir = defaultHomeForProvider(workspace.provider);
      await updateWorkingDirectory(defaultDir);
    }

    try {
      agent.setSelectedAgentId(nextAgentId);
      agent.setConnectingStatus();
      await window.freshvibe.saveSettings("selectedAgent", nextAgentId);
      await ensureAgentRunning(nextAgentId);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to start agent in workspace";
      console.error("[CloudApp] Agent start after workspace connect failed:", err);
      toast.error("Agent start failed", message);
    }
  }, [
    agent,
    ensureAgentRunning,
    queuedAgentAfterWorkspaceConnect,
    refreshWorkspaceContext,
    syncWorkspaceState,
    toast,
    updateWorkingDirectory,
    workingDirectory,
  ]);

  const handleResetDevState = useCallback(async () => {
    if (!isDevMode || resettingDevState) return;

    setResettingDevState(true);
    try {
      const response = await fetch("/api/dev/reset-user-state", {
        method: "POST",
        credentials: "include",
      });
      const data = await response.json().catch(() => ({})) as {
        success?: boolean;
        error?: string;
      };

      if (!response.ok || !data.success) {
        throw new Error(data.error || `Reset failed (${response.status})`);
      }

      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (key?.startsWith("freshvibe:")) {
          keysToRemove.push(key);
        }
      }
      for (const key of keysToRemove) {
        window.localStorage.removeItem(key);
      }
      window.sessionStorage.clear();
      window.location.assign("/app");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to clear dev state";
      toast.error("Reset failed", message);
      setResettingDevState(false);
    }
  }, [isDevMode, resettingDevState, toast]);

  // ─── Session management ───

  const handleNewChat = useCallback(
    async (_folder?: string) => {
      cancelSessionRestore();

      if (activeSessionId && chat.messages.length > 0) {
        await window.freshvibe.saveSessionMessages(activeSessionId, chat.messages);
      }
      setPersistedActiveSessionId(null);
      setActiveAcpSessionId(null);
      chat.clearMessages();
      debugLog.clear();
      // Create a new ACP session on the existing connection
      try {
        const acpSessionId = await window.freshvibe.newSession();
        pendingAcpSessionIdRef.current = acpSessionId;
        setActiveAcpSessionId(acpSessionId);
      } catch {
        pendingAcpSessionIdRef.current = null;
        setActiveAcpSessionId(null);
      }
    },
    [activeSessionId, cancelSessionRestore, chat, debugLog, setPersistedActiveSessionId],
  );

  const bindSessionId = useCallback((id: string, acpSessionId: string) => {
    persistSessions((prev) => prev.map((session) =>
      session.id === id
        ? { ...session, acpSessionId }
        : session,
    ));
  }, [persistSessions]);

  const restoreBackendSession = useCallback(async (session: ChatSession | undefined, id: string) => {
    const restored = await restoreBackendSessionState({
      session,
      id,
      getCapabilities: () => window.freshvibe.getAgentCapabilities(),
      loadSession: (sessionId) => window.freshvibe.loadSession(sessionId),
      resumeSession: (sessionId) => window.freshvibe.resumeSession(sessionId),
      newSession: () => window.freshvibe.newSession(),
      bindSessionId,
    });
    pendingAcpSessionIdRef.current = restored.acpSessionId;
    return restored;
  }, [bindSessionId]);

  const loadPersistedSessionState = useCallback(
    async (id: string, runId: number) => {
      const savedMsgs = await window.freshvibe.getSessionMessages(id).catch(() => []);
      if (!isSessionRestoreCurrent(runId)) return;

      chat.loadMessages(Array.isArray(savedMsgs) ? savedMsgs : []);
    },
    [chat, isSessionRestoreCurrent],
  );

  useEffect(() => {
    if (!isDebugTabVisible || !activeSessionId) {
      return;
    }
    if (debugLogHydratedSessionRef.current === activeSessionId) {
      return;
    }

    let cancelled = false;

    void window.freshvibe.getSessionDebugLog(activeSessionId)
      .then((savedDebug) => {
        if (cancelled) return;
        debugLog.loadEntries(Array.isArray(savedDebug) ? savedDebug : [], { merge: true });
        debugLogHydratedSessionRef.current = activeSessionId;
      })
      .catch(() => {
        if (cancelled) return;
        debugLogHydratedSessionRef.current = activeSessionId;
      });

    return () => {
      cancelled = true;
    };
  }, [activeSessionId, debugLog.loadEntries, isDebugTabVisible]);

  const handleSelectSession = useCallback(
    async (id: string) => {
      if (id === activeSessionId) return;

      // Switching away leaves the background session's prompt running on the
      // core — per-session write arbitration keeps concurrent sessions isolated,
      // so peeking at another session must not interrupt work in this one. If a
      // prompt is mid-stream the transcript still holds the "streaming"
      // placeholder, so normalise it before persisting — otherwise switching
      // back would restore a message stuck in the streaming state.
      if (activeSessionId && chat.messages.length > 0) {
        await window.freshvibe.saveSessionMessages(
          activeSessionId,
          prepareMessagesForPersistence(chat.messages),
        );
      }

      const session = sessions.find((entry) => entry.id === id);
      if (!session) return;
      const runId = startSessionRestoreRun();
      setPersistedActiveSessionId(id);
      setActiveAcpSessionId(null);
      pendingAcpSessionIdRef.current = null;
      debugLogHydratedSessionRef.current = null;
      chat.clearMessages();
      debugLog.clear();

      if (session?.projectFolder && session.projectFolder !== workingDirectory) {
        setWorkingDirectory(session.projectFolder);
      }

      if (
        session?.agentId
        && session.agentId !== "unknown"
        && session.agentId !== agent.selectedAgent
      ) {
        agent.setSelectedAgentId(session.agentId);
      }

      await loadPersistedSessionState(id, runId);
      if (!isSessionRestoreCurrent(runId)) return;

      queueSessionRestore(async () => {
        if (!isSessionRestoreCurrent(runId)) return;

        try {
          if (
            session?.projectFolder
            && session.projectFolder !== workingDirectory
          ) {
            await window.freshvibe.setWorkingDirectory(session.projectFolder);
            await window.freshvibe.saveSettings("workingDirectory", session.projectFolder);
            if (!isSessionRestoreCurrent(runId)) return;
          }

          if (
            session?.agentId
            && session.agentId !== "unknown"
            && session.agentId !== agent.selectedAgent
          ) {
            await handleChangeAgent(session.agentId);
            if (!isSessionRestoreCurrent(runId)) return;
          }

          if (session?.modelId && session.modelId !== agent.currentModelId) {
            await agent.setModel(session.modelId).catch(() => {});
            if (!isSessionRestoreCurrent(runId)) return;
          }

          const runtimeState = await window.freshvibe.getAgentState().catch(() => ({
            connected: false,
            presetId: null,
          }));
          if (!isSessionRestoreCurrent(runId)) return;

          const restorePlan = buildCloudSessionRestorePlan({
            sessionAgentId: session?.agentId,
            currentAgentId: agent.selectedAgent,
            runtimeConnected: Boolean(runtimeState?.connected),
            runtimePresetId: runtimeState?.presetId ?? null,
            acpSessionId: session?.acpSessionId ?? null,
          });
          if (restorePlan.needsEnsure && restorePlan.agentId) {
            await ensureAgentRunning(restorePlan.agentId, {
              resumeSessionId: restorePlan.resumeSessionId ?? undefined,
              reason: "reconnect",
            });
            if (!isSessionRestoreCurrent(runId)) return;
          }

          const restored = await restoreBackendSession(session, id);
          if (!isSessionRestoreCurrent(runId)) return;
          setActiveAcpSessionId(restored.acpSessionId);
        } catch {
          if (!isSessionRestoreCurrent(runId)) return;
          // If no agent is connected yet, keep the restored transcript only.
          setActiveAcpSessionId(null);
        } finally {
          if (isSessionRestoreCurrent(runId)) {
            setIsSessionRestorePending(false);
          }
        }
      });
    },
    [
      activeSessionId,
      agent.currentModelId,
      agent.selectedAgent,
      agent.setModel,
      agent.setSelectedAgentId,
      chat,
      debugLog,
      handleChangeAgent,
      ensureAgentRunning,
      isSessionRestoreCurrent,
      loadPersistedSessionState,
      queueSessionRestore,
      restoreBackendSession,
      sessions,
      setPersistedActiveSessionId,
      startSessionRestoreRun,
      workingDirectory,
    ],
  );

  useEffect(() => {
    if (loading || appView !== "app") return;

    const restoreSessionId = bootRestoreSessionIdRef.current;
    if (!restoreSessionId) return;

    if (!sessions.some((session) => session.id === restoreSessionId)) {
      bootRestoreSessionIdRef.current = null;
      void window.freshvibe.saveSettings("activeSessionId", null);
      return;
    }

    bootRestoreSessionIdRef.current = null;
    void handleSelectSession(restoreSessionId);
  }, [appView, handleSelectSession, loading, sessions]);

  const handleDeleteSession = useCallback(
    (id: string) => {
      setSessions((prev) => {
        const updated = prev.filter((s) => s.id !== id);
        window.freshvibe.saveSettings("chatSessions", updated);
        return updated;
      });
      window.freshvibe.deleteSessionMessages(id);
      window.freshvibe.deleteSessionDebugLog(id);
      if (id === activeSessionId) {
        cancelSessionRestore();
        pendingAcpSessionIdRef.current = null;
        setPersistedActiveSessionId(null);
        setActiveAcpSessionId(null);
        debugLogHydratedSessionRef.current = null;
        chat.clearMessages();
        debugLog.clear();
      }
    },
    [activeSessionId, cancelSessionRestore, chat, debugLog, setPersistedActiveSessionId],
  );

  const handleNewProject = useCallback(
    async (dir: string) => {
      cancelSessionRestore();

      if (activeSessionId && chat.messages.length > 0) {
        await window.freshvibe.saveSessionMessages(activeSessionId, chat.messages);
      }

      setWorkingDirectory(dir);
      await window.freshvibe.setWorkingDirectory(dir);
      await window.freshvibe.saveSettings("workingDirectory", dir);
      try {
        const acpSessionId = await window.freshvibe.newSession();
        pendingAcpSessionIdRef.current = acpSessionId;
        setActiveAcpSessionId(acpSessionId);
      } catch {
        pendingAcpSessionIdRef.current = null;
        setActiveAcpSessionId(null);
      }

      setPersistedActiveSessionId(null);
      chat.clearMessages();
      debugLogHydratedSessionRef.current = null;
      debugLog.clear();
      setShowNewProject(false);
    },
    [activeSessionId, cancelSessionRestore, chat, debugLog, setPersistedActiveSessionId],
  );

  let devToolsEntries: DevDebugEntry[] | undefined;
  if (isDevMode) {
    const storedSettings = window.localStorage.getItem("freshvibe:settings");
    const selectedWorkspaceLabel = selectedWorkspace
      ? `${providerLabel(selectedWorkspace.provider)} · ${selectedWorkspace.resourceId}`
      : "none";

    devToolsEntries = [
      {
        label: "User",
        value: user ? `${user.email} (${user.id})` : "loading",
        tone: "accent",
      },
      {
        label: "Onboarding",
        value: `${appView} · ${setupComplete ? "complete" : "pending"}`,
        tone: appView === "app" ? "default" : "warn",
      },
      {
        label: "Agent",
        value: `${agent.selectedAgent || "none"} · ${agent.status}`,
      },
      {
        label: "Workspace",
        value: selectedWorkspaceLabel,
      },
      {
        label: "Working Directory",
        value: workingDirectory || "none",
      },
      ...(portProxyMetadata ? [{
        label: "Port URLs",
        value: portProxyMetadata.slugs.map((s) => `${s}.${portProxyMetadata.domain}`).join(", "),
        tone: "accent" as const,
      }] : []),
      {
        label: "Terminal",
        value: `${terminalStatus} · bottom panel ${showBottomPanel ? bottomTab : "hidden"}`,
      },
      {
        label: "Saved Sessions",
        value: `${sessions.length} total${activeSessionId ? ` · active ${activeSessionId}` : ""}`,
      },
      {
        label: "Settings Snapshot",
        value: formatDebugValue(parseStoredJson(storedSettings)),
      },
    ];
  }

  // ─── Loading ───
  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-navy-dark flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  // ─── Not authenticated ───
  if (!user) {
    return (
      <div className="min-h-[100dvh] bg-navy-dark flex items-center justify-center">
        <div className="text-center">
          <p className="font-body text-stone-400 mb-4">Unable to authenticate</p>
          <a
            href="/api/auth/login"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gold text-navy font-display font-semibold text-sm hover:bg-gold-dark transition-all"
          >
            Sign in
          </a>
        </div>
      </div>
    );
  }

  if (appView === "onboarding") {
    return (
      <WebOnboardingFlow
        draft={onboardingDraft}
        onDraftChange={(updater) => setOnboardingDraft((prev) => updater(prev))}
        onStart={handleStartOnboarding}
        submitting={false}
        error={bootState?.error ?? null}
        providerMode={effectiveProvider}
        userEmail={user?.email}
        isAdmin={isAdmin}
      />
    );
  }

  if (workspaceBootOverlay) {
    return (
      <WorkspaceBootScreen
        mode={workspaceBootOverlay.mode}
        progress={workspaceBootOverlay.state.progress}
        headline={workspaceBootOverlay.state.headline}
        detail={workspaceBootOverlay.state.detail}
        status={agent.status}
        installProgress={overlayInstallProgress}
        authChallenge={agent.authChallenge}
        error={workspaceBootOverlay.state.error}
        errorDetails={workspaceBootOverlay.errorDetails}
        onRetry={workspaceBootOverlay.onRetry}
        onChooseWorkspace={handleChooseDifferentWorkspace}
      />
    );
  }

  // ─── Main chat UI with sidebar ───
  return (
    <div className="h-[100dvh] bg-[#021a26] flex relative overflow-clip">
      {/* Ambient background glow — gives the glass sidebar something to refract */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 rounded-full bg-[#1a3a5c]/40 blur-[120px]" />
        <div className="absolute top-1/3 -left-[10%] w-2/5 h-2/5 rounded-full bg-[#0d4a4a]/30 blur-[100px]" />
        <div className="absolute -bottom-1/4 left-1/4 w-1/3 h-1/3 rounded-full bg-[#2a1a4a]/25 blur-[120px]" />
      </div>
      {/* Sidebar */}
      <Sidebar
        sessions={sessions}
        activeSessionId={activeSessionId}
        workingDirectory={workingDirectory}
        onSelectSession={handleSelectSession}
        onDeleteSession={handleDeleteSession}
        onNewChat={handleNewChat}
        onNewProject={() => setShowNewProject(true)}
        onSettingsClick={() => setShowSettings(true)}
        userProfile={user}
        onAuthLogout={handleAuthLogout}
        workspaceTarget={{
          status: hasWorkspace ? "connected" : "none",
          terminalStatus,
          agentStatus: agent.status,
          providerName: providerLabel(selectedWorkspace?.provider || onboardingDraft.createProvider),
          detailsLabel: selectedWorkspace
            ? workspaceStatusDetail(selectedWorkspace)
            : undefined,
          onOpenDialog: () => setWorkspaceDialogVisible(true),
        }}
        devTools={
          isDevMode && devToolsEntries
            ? {
                title: "Web dev state",
                entries: devToolsEntries,
                onResetState: handleResetDevState,
                resetLabel: "Clear user state",
                resetting: resettingDevState,
              }
            : undefined
        }
        mobileOpen={sidebarOpen}
        onMobileClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
        uiVariant="web"
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* StatusBar */}
        <StatusBar
          onMenuToggle={() => {
            if (window.matchMedia("(min-width: 768px)").matches) {
              setSidebarCollapsed(c => !c);
            } else {
              setSidebarOpen(true);
            }
          }}
          menuOpen={!sidebarCollapsed}
          agentId={agent.selectedAgent}
          status={agent.status}
          workingDirectory={workingDirectory}
          availableModels={agent.availableModels}
          currentModelId={agent.currentModelId}
          isRefreshingModels={agent.isRefreshingModels}
          harnessStates={agent.harnessStates}
          onChangeAgent={handleChangeAgent}
          onChangeModel={handleChangeModel}
          onRefreshModels={agent.refreshModels}
          onLogout={handleLogout}
          onReconnect={handleReconnect}
          onDebugToggle={() => setShowBottomPanel(!showBottomPanel)}
          debugVisible={showBottomPanel}
          showOpenProjectButton={false}
          preview={previewState}
          previewPort={previewPort}
          previewVisible={showPreview}
          onPreviewToggle={openPreview}
          previewUi={previewUi}
          onPreviewAction={handlePreviewAction}
        />

        {/* Messages area — reuses MessageList from Electron app */}
        <MessageList
          messages={chat.messages}
          isStreaming={chat.isStreaming}
          isThinking={chat.isThinking}
          thinkingContent={chat.thinkingContent}
          activeTool={chat.activeTool}
          activeToolCall={chat.activeToolCall}
          completedToolCalls={chat.completedToolCalls}
          pendingPermission={chat.pendingPermission}
          onPermissionResponse={chat.respondToPermission}
          onAlwaysApprovePermission={chat.alwaysApprovePendingPermission}
          scrollBehavior={isSessionRestorePending ? "auto" : "smooth"}
          isLoading={isSessionRestorePending}
        />

        {/* Input */}
        <InputArea
          onSend={chat.sendMessage}
          onStop={chat.cancelPrompt}
          onForceStop={() => agent.stopAgent()}
          disabled={
            (chat.isStreaming && !chat.pendingPermission)
            || isSessionRestorePending
            || activeAcpSessionId === null
          }
          isStreaming={chat.isStreaming}
          isCancelling={chat.isCancelling}
          agentStatus={agent.status}
          availableCommands={chat.availableCommands}
          uiVariant="web"
        />

        {/* Bottom panel: terminal + debug */}
        <BottomPanel
          visible={showBottomPanel}
          debugEntries={debugLog.entries}
          onClearDebug={debugLog.clear}
          onClose={() => setShowBottomPanel(false)}
          activeTab={bottomTab}
          onTabChange={setBottomTab}
          authActive={Boolean(agent.authChallenge)}
        />
      </div>

      {/* Preview side panel — iframe showing the project's port-proxy URL */}
      {showPreview && previewUrl && (
        <div className="hidden lg:flex flex-col border-l border-navy-light bg-navy-dark" style={{ width: "45%" }}>
          {/* Preview header */}
          <div className="flex items-center justify-between px-3 h-11 border-b border-navy-light bg-navy shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0 animate-pulse" />
              <span className="font-mono text-xs text-stone-400 truncate">{previewHost}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => window.open(previewUrl, "_blank")}
                className="p-1.5 rounded-md text-stone-400 hover:text-white hover:bg-navy-light transition-colors"
                title="Open in new tab"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </button>
              <button
                onClick={() => {
                  // Force iframe reload
                  const iframe = document.querySelector<HTMLIFrameElement>("#preview-iframe");
                  if (iframe) iframe.src = iframe.src;
                }}
                className="p-1.5 rounded-md text-stone-400 hover:text-white hover:bg-navy-light transition-colors"
                title="Reload preview"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182M2.985 19.644l3.18-3.18" />
                </svg>
              </button>
              <button
                onClick={() => setShowPreview(false)}
                className="p-1.5 rounded-md text-stone-400 hover:text-white hover:bg-navy-light transition-colors"
                title="Close preview"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {/* iframe */}
          <iframe
            id="preview-iframe"
            src={previewUrl}
            className="flex-1 w-full bg-white"
            title="Preview"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      )}

      {/* New project dialog */}
      <NewProjectDialog
        visible={showNewProject}
        onClose={() => setShowNewProject(false)}
        onProjectCreated={handleNewProject}
        canChangeStarterCloneMode={isAdmin}
      />

      {/* Settings panel */}
      <SettingsPanel
        visible={showSettings}
        currentAgent={agent.selectedAgent}
        workingDirectory={workingDirectory}
        harnessStates={agent.harnessStates}
        onClose={() => setShowSettings(false)}
        onChangeAgent={handleChangeAgent}
        onChangeDirectory={handleChangeDirectory}
        claudeProvider={null}
        onClaudeProviderChange={() => {}}
        onRunAwsLogin={() => {}}
        onRunTerminalCommand={() => {}}
        onInstallAgent={async (agentId) => {
          if (!hasWorkspace) {
            setQueuedAgentAfterWorkspaceConnect(agentId);
            agent.setSelectedAgentId(agentId);
            await window.freshvibe.saveSettings("selectedAgent", agentId);
            setWorkspaceDialogVisible(true);
            toast.warning(
              "Workspace required",
              "Create or connect a workspace before installing an agent.",
            );
            return false;
          }
          return agent.installAgent(agentId);
        }}
        onStopAgent={agent.stopAgent}
        onRefreshHarness={agent.refreshHarnessStates}
        permissionPresets={Object.values(chat.permissionPresets)}
        onDeletePermissionPreset={chat.deletePermissionPreset}
      />

      <TeamWorkspaceDialog
        visible={workspaceDialogVisible}
        onClose={() => setWorkspaceDialogVisible(false)}
        onConnected={handleWorkspaceConnected}
        providerMode={selectedWorkspace?.provider ?? onboardingDraft.createProvider}
      />

      {/* Auth challenge */}
      {agent.status === "authenticating" && agent.authChallenge && showBottomPanel && (
        <TerminalAuthCard challenge={agent.authChallenge} />
      )}
      {agent.status === "authenticating" && agent.authChallenge && !showBottomPanel && (
        <AuthDialog
          challenge={agent.authChallenge}
          onShowTerminal={() => { setShowBottomPanel(true); setBottomTab("terminal"); }}
          onDismiss={agent.dismissAuthChallenge}
        />
      )}

      {/* Toast notifications */}
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  );
}
